import { ExperienceItem } from '../types';

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

export function isValidDate(d: Date): boolean {
  return !Number.isNaN(d.getTime());
}

export function parseSafeDate(dateStr: string | Date | null | undefined): Date {
  if (!dateStr) return new Date();
  if (dateStr instanceof Date) return isValidDate(dateStr) ? dateStr : new Date();
  const parts = String(dateStr).split('-');
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1] || '1', 10) - 1;
  const day = parseInt(parts[2] || '1', 10);
  const parsed = new Date(year, month, day);
  return isValidDate(parsed) ? parsed : new Date();
}

export function formatMonthYear(date: Date | string): string {
  const d = date instanceof Date ? date : parseSafeDate(date);
  return `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
}

export function isLastDayOfMonth(d: Date): boolean {
  const nextDay = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return d.getDate() === nextDay.getDate();
}

export function calculateMonths(start: Date, end: Date): number {
  let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
  const isEndMonthEnd = isLastDayOfMonth(end);
  const isStartMonthEnd = isLastDayOfMonth(start);

  if (end.getDate() < start.getDate() && !(isEndMonthEnd && isStartMonthEnd)) {
    months -= 1;
  }
  return months < 0 ? 0 : months;
}

/**
 * Returns a duration label like "2y 1mo" between start and end (or now).
 */
export function formatDuration(startDate: string | Date, endDate: string | Date = new Date()): string {
  const start = parseSafeDate(startDate);
  const end = parseSafeDate(endDate);

  const months = calculateMonths(start, end);

  const years = Math.floor(months / 12);
  const remMonths = months % 12;

  const parts: string[] = [];
  if (years > 0) parts.push(`${years}y`);
  if (remMonths > 0 || years === 0) parts.push(`${remMonths}mo`);
  return parts.join(' ');
}

/**
 * Builds "Nov 2025 — Present · 9mo" style label for a timeline item.
 */
export function formatWhen(startDate: string, endDate: string | null): string {
  const start = parseSafeDate(startDate);
  const end = endDate ? parseSafeDate(endDate) : new Date();
  const startLabel = formatMonthYear(start);
  const endLabel = endDate ? formatMonthYear(end) : 'Present';
  const duration = formatDuration(start, end);
  return `${startLabel} — ${endLabel} · ${duration}`;
}

export function totalExperience(experienceList: readonly ExperienceItem[]): string {
  if (!experienceList || experienceList.length === 0) return '0mo';

  const intervals = experienceList
    .map((item) => ({
      start: parseSafeDate(item.start),
      end: item.end ? parseSafeDate(item.end) : new Date(),
    }))
    .filter((inv) => inv.start <= inv.end)
    .sort((a, b) => a.start.getTime() - b.start.getTime());

  if (intervals.length === 0) return '0mo';

  // Merge overlapping intervals
  const merged: Array<{ start: Date; end: Date }> = [];
  for (const curr of intervals) {
    const last = merged[merged.length - 1];
    if (last && curr.start <= last.end) {
      last.end = new Date(Math.max(last.end.getTime(), curr.end.getTime()));
    } else {
      merged.push({ start: new Date(curr.start), end: new Date(curr.end) });
    }
  }

  let totalMonths = 0;
  for (const interval of merged) {
    totalMonths += calculateMonths(interval.start, interval.end);
  }

  const years = Math.floor(totalMonths / 12);
  const remMonths = totalMonths % 12;
  const parts: string[] = [];
  if (years > 0) parts.push(`${years}y`);
  if (remMonths > 0 || years === 0) parts.push(`${remMonths}mo`);
  return parts.join(' ');
}
